/**
 * UnifiedSSEProviderV3 - Fixed version with stable selectors
 * No infinite loops, no re-render issues
 */

import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { WORKER_API_URL } from '../../../api/config';
import { fetchWithRetry } from '../../../utils/fetchWithRetry';
import { useChatStore } from '../../../stores/chatStore';
import { useChatActions } from '../../../hooks/chat/useStableChatSelectors';
import { conversationService } from '../../../api/conversationService';
import { threadService } from '../../../api/threadService';

interface UnifiedSSEProviderV3Props {
  children: React.ReactNode;
}

export const UnifiedSSEProviderV3: React.FC<UnifiedSSEProviderV3Props> = ({ children }) => {
  const { token } = useAuth();
  const activeStreamRef = useRef<AbortController | null>(null);
  const isTestMode = typeof window !== 'undefined' && window.location.pathname.startsWith('/chat-test');
  // Always persist assistant on FE as a safety net (hard-coded true per request)
  // FE assistant persist is disabled by default; backend finalize/persist is authoritative.
  const FE_PERSIST_ASSISTANT: boolean = (() => {
    try {
      const v = (import.meta as any)?.env?.VITE_FE_PERSIST_ASSISTANT;
      if (typeof v === 'string') {
        const s = v.toLowerCase();
        return s === '1' || s === 'true' || s === 'yes';
      }
    } catch {}
    // Default OFF to avoid double-persist duplicates; the draft remains visible regardless
    return false;
  })();
  // Optional UI smoothing for bursty SSE: batch tokens and append at small intervals
  const SMOOTH_MS = Number((import.meta as any)?.env?.VITE_SSE_SMOOTH_STREAM_MS || 30);
  const tokenQueueRef = useRef<string[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startFlusher = useRef<() => void>(() => {});
  // Verbose stream logs toggle (default off)
  const VERBOSE_STREAM_LOGS: boolean = (() => {
    try {
      const v = (import.meta as any)?.env?.VITE_STREAM_VERBOSE_LOGS;
      return typeof v === 'string' && (v === '1' || v.toLowerCase() === 'true');
    } catch {}
    return false;
  })();
  
  // Use stable selectors - get individual values to avoid object dependency issues
  const isStreaming = useChatStore(
    (state) => state.currentStream?.status === 'streaming'
  );
  const conversationRef = useChatStore(
    (state) => state.currentStream?.conversationRef
  );
  const streamError = useChatStore(
    (state) => state.currentStream?.error
  );
  
  const actions = useChatActions();
  // Stable debug instance ID per provider mount (helps trace logs)
  const providerInstanceIdRef = useRef<string>(`prov-${Math.random().toString(36).slice(2, 8)}`);
  const quickReconcileAfterDone = async (convRef?: string) => {
    try {
      const ref = convRef || conversationRefRef.current;
      if (!ref) return;
      // Determine if this conversation is branching (has main_thread_id)
      let mainThreadId: string | undefined;
      try {
        const conv = await conversationService.getConversationById(ref);
        mainThreadId = (conv as any)?.main_thread_id || undefined;
      } catch {
        mainThreadId = undefined;
      }

      if (mainThreadId) {
        // Branching path: hydrate from ThreadView to pick up assistant finalize
        const view = await threadService.getThreadView(mainThreadId);
        const now = Date.now();
        const normalized = (view?.messages || []).map((m: any, idx: number) => ({
          id: String(m.id ?? ''),
          clientId: String(m.id ?? `msg-${idx}`),
          conversationRef: ref,
          role: (m.role === 'tool' ? 'system' : (m.role as any)),
          content: (m?.content && (m as any).content?.text) || JSON.stringify(m?.content || {}),
          createdAt: new Date(now + idx).toISOString(),
          status: 'saved' as const,
        }));
        useChatStore.getState().hydrateConversation(ref, normalized, false, undefined);
      } else {
        // Linear/legacy path: fetch recent conversation messages
        const recent = await conversationService.getChatMessages(ref, 0, 10);
        if (!Array.isArray(recent)) return;
        const normalized = recent.map((msg: any) => ({
          id: (msg.message_id ?? msg.id ?? '').toString(),
          clientId: (msg.client_id ?? `msg-${msg.message_id ?? msg.id}`) + '',
          conversationRef: ref,
          role: (msg.sender_role ?? msg.role) as 'user' | 'assistant' | 'system',
          content: msg.message_content ?? msg.content ?? '',
          createdAt: String(msg.timestamp ?? msg.created_at ?? new Date().toISOString()),
          status: 'saved' as const,
        }));
        useChatStore.getState().hydrateConversation(ref, normalized, false, undefined);
      }
    } catch (e) {
      console.warn('[UnifiedSSEProviderV3] Quick reconcile failed:', e);
    }
  };
  
  // Idempotent reconcile/persist after stream completes.
  // If Backend finalize already saved the assistant message, reconcile the draft to it.
  // Otherwise, persist via FE and reconcile.
  const reconcileOrPersistAssistant = async (): Promise<void> => {
    try {
      const st = useChatStore.getState();
      const convRef = conversationRefRef.current;
      const draftId = st.currentStream?.draftClientId;
      // If this was a side (threaded) stream OR explicitly marked sideStream,
      // avoid hydrating/reconciling the main lane after completion
      if (st.currentStream?.threadId || st.currentStream?.sideStream) {
        return;
      }
      if (!convRef || !draftId) return;
      const draftMsg = st.messagesById[draftId];
      const content = (draftMsg?.content || '').trim();
      if (!content) return;

      // Detect branching/thread targeting
      const targetedThreadId = st.currentStream?.threadId;
      let mainThreadId: string | undefined;
      if (!targetedThreadId) {
        try {
          const conv = await conversationService.getConversationById(convRef);
          mainThreadId = (conv as any)?.main_thread_id || undefined;
        } catch {
          mainThreadId = undefined;
        }
      } else {
        mainThreadId = undefined;
      }

      // Search for an existing assistant message with identical content
      let existingId: string | undefined;
      const threadToCheck = targetedThreadId || mainThreadId;
      if (threadToCheck) {
        const view = await threadService.getThreadView(threadToCheck);
        const msgs = Array.isArray(view?.messages) ? view.messages : [];
        for (let i = msgs.length - 1; i >= 0; i--) {
          const m: any = msgs[i];
          const role = m?.role === 'tool' ? 'system' : m?.role;
          const mContent = (m?.content && (m as any).content?.text) || JSON.stringify(m?.content || {}) || '';
          if (role === 'assistant' && (mContent || '').trim() === content) {
            existingId = String(m.id);
            break;
          }
        }
      } else {
        const recent = await conversationService.getChatMessages(convRef, 0, 10);
        for (let i = recent.length - 1; i >= 0; i--) {
          const m: any = recent[i];
          const role = (m?.sender_role ?? m?.role) as string;
          const mContent = (m?.message_content ?? m?.content ?? '') as string;
          if (role === 'assistant' && (mContent || '').trim() === content) {
            existingId = String(m.message_id ?? m.id);
            break;
          }
        }
      }

      const reconcile = useChatStore.getState().reconcileMessageId;
      if (existingId) {
        reconcile(draftId, existingId);
        return;
      }

      if (FE_PERSIST_ASSISTANT) {
        try {
          const saved = await conversationService.createChatMessage(convRef, {
            conversation_id: 0 as any,
            sender_role: 'assistant' as any,
            message_content: content,
          });
          reconcile(draftId, String(saved.message_id));
        } catch (e) {
          console.warn('[UnifiedSSEProviderV3] Failed to persist assistant message (FE persist):', e);
        }
      }
    } catch (e) {
      console.warn('[UnifiedSSEProviderV3] reconcileOrPersistAssistant failed:', e);
    }
  };
  
  // Store conversation ref in a ref to access in async function
  const conversationRefRef = useRef(conversationRef);
  useEffect(() => {
    conversationRefRef.current = conversationRef;
  }, [conversationRef]);
  
  // Effect to handle streaming when triggered by Zustand
  useEffect(() => {
    console.log('[UnifiedSSEProviderV3] 🔍 STREAM STATE CHECK:', {
      isStreaming,
      conversationRef,
      hasToken: !!token,
      isTestMode,
      timestamp: new Date().toISOString()
    });
    
    // Explicit guard: require Authorization unless test mode
    if (!isStreaming || !conversationRef) {
      console.log('[UnifiedSSEProviderV3] ⏸️ Not streaming or no conversationRef; skipping');
      return;
    }
    if (!isTestMode && !token) {
      console.warn('[UnifiedSSEProviderV3] ⛔ Missing auth token; not starting stream', { conversationRef });
      return;
    }
    
    const performStreaming = async () => {
      console.log('[UnifiedSSEProviderV3] 🚀 STARTING STREAM PERFORMANCE');

      // Abort any existing stream
      if (activeStreamRef.current) {
        console.log('[UnifiedSSEProviderV3] 🛑 Aborting existing stream');
        activeStreamRef.current.abort();
      }

      const abortController = new AbortController();
      activeStreamRef.current = abortController;

      // Reset any residual token buffer/flush state to prevent cross-stream leakage
      try {
        if (flushTimerRef.current) {
          clearInterval(flushTimerRef.current);
          flushTimerRef.current = null;
        }
      } catch {}
      tokenQueueRef.current = [];
      
      try {
        const convRef = conversationRefRef.current;
        console.log('[UnifiedSSEProviderV3] 📝 Stream details:', {
          conversationRef: convRef,
          endpoint: isTestMode ? 'chat-test' : 'chat',
          timestamp: new Date().toISOString()
        });
        // Unified path: always stream via Worker /stream/chat.
        // Worker auto-detects branching by conversation_ref and normalizes SSE.
        // Removing the early-return prevents missing /stream/chat on branching convs.
        
        // Get current messages for context - do this outside of React lifecycle
        // to avoid subscription issues. Then prune to avoid oversized payloads.
        const state = useChatStore.getState();
        const messageIds = state.messageIdsByConvRef[convRef] || [];
        const rawMessages = messageIds
          .map(id => state.messagesById[id])
          .filter(Boolean)
          // Exclude assistant draft/empty messages to keep last role as user
          .filter(msg => !(msg.role === 'assistant' && (msg.isDraft || !msg.content)))
          .map(msg => ({
            role: msg.role,
            content: msg.content
          }));

        // Prune messages to a safe window to prevent 413/timeout on long threads
        const MAX_CONTEXT_CHARS = 30000; // ~30KB payload budget
        const MAX_CONTEXT_MESSAGES = 30; // cap number of messages
        const pruneMessages = (list: { role: string; content: string }[]) => {
          if (!Array.isArray(list) || list.length === 0) return [] as typeof list;
          let remaining = MAX_CONTEXT_CHARS;
          const acc: { role: string; content: string }[] = [];
          for (let i = list.length - 1; i >= 0 && acc.length < MAX_CONTEXT_MESSAGES; i--) {
            const m = list[i];
            const len = (m.content?.length || 0) + 32; // include small overhead per message
            if (len <= remaining || acc.length === 0) {
              acc.push(m);
              remaining -= len;
            } else {
              break;
            }
          }
          return acc.reverse();
        };

        const messages = pruneMessages(rawMessages);
        // Derive last user message for v2 minimal contract. Prefer pending override
        // when a side (split) stream initiated the request.
        let lastUserMessage = '';
        try {
          const pending = useChatStore.getState().currentStream?.pendingUserText;
          if (pending && pending.trim()) {
            lastUserMessage = pending;
          }
        } catch {}
        if (!lastUserMessage) {
          for (let i = rawMessages.length - 1; i >= 0; i--) {
            const m = rawMessages[i];
            if (m && m.role === 'user' && (m.content || '').trim()) { lastUserMessage = m.content; break; }
          }
        }
        
        const endpoint = isTestMode ? `${WORKER_API_URL}/stream/chat-test` : `${WORKER_API_URL}/stream/chat`;
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        };
        if (!isTestMode && token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        // Correlation and diagnostics headers
        try {
          const reqId = useChatStore.getState().currentStream?.requestId;
          const conv = conversationRefRef.current;
          if (reqId) headers['X-Client-Request-Id'] = reqId;
          if (conv) headers['X-Conversation-Ref'] = conv;
          headers['X-Debug-Provider-Instance'] = String(providerInstanceIdRef.current);
        } catch {}

        // Optional thread targeting for branched chat
        const threadId = (() => {
          try { return useChatStore.getState().currentStream?.threadId || undefined; } catch { return undefined; }
        })();
        const parentMessageId = (() => {
          try { return useChatStore.getState().currentStream?.parentMessageId || undefined; } catch { return undefined; }
        })();

        const routing = threadId ? 'threaded' : 'linear';
        // Build request body per routing mode:
        // - threaded (split): minimal v2 body, no messages array
        // - linear (main): include legacy messages for context/back-compat
        const requestBody: Record<string, any> = threadId
          ? {
              conversation_ref: convRef,
              threadId,
              // Ensure backend appends user under the correct anchor
              ...(parentMessageId ? { parentMessageId } : {}),
              last_user_message: lastUserMessage,
              use_tools: false,
              stream: true,
            }
          : {
              // v2 minimal contract
              conversation_ref: convRef,
              last_user_message: lastUserMessage,
              use_tools: false,
              stream: true,
              // legacy v1 fields (kept for fallback compatibility in linear mode only)
              conversation_id: convRef,
              messages,
            };
        if (process.env.NODE_ENV !== 'production') {
          console.log('[UnifiedSSEProviderV3] 📤 Sending request:', {
            endpoint,
            messagesCount: messages.length,
            lastMessage: messages[messages.length - 1],
            routing,
            threadId,
            parentMessageId,
            bodySize: JSON.stringify(requestBody).length,
            // Shape confirmations for debug
            hasThreadId: Boolean((requestBody as any).threadId),
            hasMessagesArray: Array.isArray((requestBody as any).messages),
            includesConversationIdLegacy: Object.prototype.hasOwnProperty.call(requestBody, 'conversation_id'),
            lastUserMessageLength: typeof (requestBody as any).last_user_message === 'string' ? (requestBody as any).last_user_message.length : 0,
            timestamp: new Date().toISOString()
          });
        }

        // Metrics: measure request latency
        const metricsEnabled = (import.meta as any)?.env?.VITE_LOG_STREAM_METRICS;
        const requestStart = performance.now();
        const response = await fetchWithRetry(
          endpoint,
          {
            method: 'POST',
            headers,
            // Send minimal v2 body for threaded; include legacy fields only for linear mode
            body: JSON.stringify(requestBody),
            signal: abortController.signal,
            maxRetries: 3,
            initialDelay: 1000,
          }
        );
        
        // Try to capture server-provided request IDs for correlation
        const serverReqId = response.headers.get('X-Request-Id');
        const echoedClientReqId = response.headers.get('X-Client-Request-Id');
        const contentType = response.headers.get('Content-Type') || response.headers.get('content-type') || '';
        if (process.env.NODE_ENV !== 'production') {
          console.log('[UnifiedSSEProviderV3] 📥 Response received:', {
            status: response.status,
            ok: response.ok,
            hasBody: !!response.body,
            headers: Object.fromEntries(response.headers.entries()),
            serverReqId,
            echoedClientReqId,
            contentType,
            timestamp: new Date().toISOString()
          });
        }

        const isSse = /text\/event-stream/i.test(contentType);
        if (!response.ok || !response.body || !isSse) {
          // Try to produce a helpful error message for common cases like 413
          let friendly = `Stream request failed: ${response.status}`;
          try {
            const txt = await response.text();
            if (response.ok && !isSse) {
              friendly = `Unexpected non-SSE response (${contentType || 'unknown'}) - ${txt.substring(0, 200)}`;
            } else if (response.status === 413) {
              friendly = 'Your message or conversation is too large. Try shortening your input or starting a new chat to reduce context size.';
            } else if (txt) {
              friendly = `${friendly} - ${txt.substring(0, 200)}`;
            }
          } catch {}
          actions.setStreamError(friendly);
          // Keep the assistant draft visible; mark as error instead of removing
          try {
            useChatStore.setState((state) => {
              const draftId = state.currentStream?.draftClientId;
              if (!draftId) return {} as any;
              const draft = state.messagesById[draftId];
              if (!draft) return {} as any;
              return {
                messagesById: {
                  ...state.messagesById,
                  [draftId]: { ...draft, isDraft: false, status: 'error' as const },
                },
              } as any;
            });
          } catch {}
          throw new Error(friendly);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let totalChunksReceived = 0;
        let totalBytesReceived = 0;
        let sseEventCount = 0;
        // Metrics accumulators
        let firstByteAt: number | undefined;
        let firstContentAt: number | undefined;
        let doneAt: number | undefined;
        let contentEvents = 0;
        const headerWorkerMode = response.headers.get('X-Worker-Chat-Mode') || response.headers.get('x-worker-chat-mode') || '';
        const headerThreadId = response.headers.get('X-Thread-Id') || response.headers.get('x-thread-id') || '';
        const headerReqId = response.headers.get('X-Request-Id') || response.headers.get('x-request-id') || '';
        const headerClientReqId = response.headers.get('X-Client-Request-Id') || response.headers.get('x-client-request-id') || '';

        if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 🔄 Starting to read stream...');

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] ✅ Stream reading complete:', {
              totalChunks: totalChunksReceived,
              totalBytes: totalBytesReceived,
              sseEvents: sseEventCount,
              timestamp: new Date().toISOString()
            });
            break;
          }
          
          totalChunksReceived++;
          totalBytesReceived += value.byteLength;
          if (firstByteAt === undefined) firstByteAt = performance.now();
          
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          
          if (VERBOSE_STREAM_LOGS) {
            console.log(`[UnifiedSSEProviderV3] 📦 Chunk ${totalChunksReceived}:`, {
              byteLength: value.byteLength,
              chunkPreview: chunk.substring(0, 100),
              bufferLength: buffer.length,
              timestamp: new Date().toISOString()
            });
          }

          // Split SSE events by double newline; keep trailing partial in buffer
          // Support CRLF and LF separators between SSE frames
          const events = buffer.split(/\r?\n\r?\n/);
          buffer = events.pop() || '';

          for (const event of events) {
            sseEventCount++;
            if (VERBOSE_STREAM_LOGS) console.log(`[UnifiedSSEProviderV3] 🎯 SSE Event ${sseEventCount}:`, {
              eventLength: event.length,
              eventPreview: event.substring(0, 50),
              timestamp: new Date().toISOString()
            });

            // Parse multi-line SSE frames; accumulate all data: lines
            const lines = event.split(/\r?\n/);
            let dataPayload = '';
            for (const l of lines) {
              const trimmed = l.trimStart();
              if (trimmed.startsWith('data:')) {
                dataPayload += trimmed.slice(5).trimStart();
              }
            }
            
            if (!dataPayload) {
              if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] ⚠️ Empty data payload, skipping');
              continue;
            }

            const dataStr = dataPayload.trim();
            if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 📋 Data payload:', {
              length: dataStr.length,
              preview: dataStr.substring(0, 100),
              isDone: dataStr === '[DONE]',
              timestamp: new Date().toISOString()
            });

            if (dataStr === '[DONE]') {
              if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 🏁 [DONE] received, completing stream');
              actions.completeStream();
              // Reconcile with backend persistence and then hydrate latest state (non-blocking)
              void (async () => {
                try {
                  await reconcileOrPersistAssistant();
                  await quickReconcileAfterDone();
                } catch {}
              })();
              if (metricsEnabled) {
                const ttfbMs = firstByteAt !== undefined ? firstByteAt - requestStart : undefined;
                const timeToFirstContentMs = firstContentAt !== undefined ? firstContentAt - requestStart : undefined;
                const totalDurationMs = performance.now() - requestStart;
                // eslint-disable-next-line no-console
                console.table({
                  ttfbMs: ttfbMs?.toFixed(1),
                  timeToFirstContentMs: timeToFirstContentMs?.toFixed(1),
                  totalDurationMs: totalDurationMs?.toFixed(1),
                  contentEvents,
                  bytesRead: totalBytesReceived,
                  workerMode: headerWorkerMode || 'unknown',
                  requestId: headerReqId || headerClientReqId || 'n/a',
                });
              }
              return;
            }

            try {
              const parsed = JSON.parse(dataStr);
              if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 🔍 Parsed SSE data:', {
                type: parsed.type,
                chunk_type: parsed.chunk_type,
                hasContent: !!parsed.content,
                contentLength: parsed.content?.length || 0,
                timestamp: new Date().toISOString()
              });

              // Append token with optional smoothing to avoid "all at once" bursts
              const appendNow = (tok: string) => {
                if (!tok) return;
                if (SMOOTH_MS > 0) {
                  tokenQueueRef.current.push(tok);
                  if (!flushTimerRef.current) {
                    flushTimerRef.current = setInterval(() => {
                      const next = tokenQueueRef.current.shift();
                      if (next) {
                        actions.appendToStream(next);
                      } else {
                        if (flushTimerRef.current) {
                          clearInterval(flushTimerRef.current);
                          flushTimerRef.current = null;
                        }
                      }
                    }, SMOOTH_MS);
                  }
                } else {
                  if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] ✍️ APPENDING TO STREAM:', {
                    tokenLength: tok.length,
                    tokenPreview: tok.substring(0, 50),
                    timestamp: new Date().toISOString()
                  });
                  actions.appendToStream(tok);
                  if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] ✅ Stream append complete');
                }
              };

              if (parsed.type === 'error') {
                console.error('[UnifiedSSEProviderV3] ❌ Stream error received:', parsed.error);
                actions.setStreamError(parsed.error || 'Unknown streaming error');
                // Keep the message visible; mark the draft as error
                try {
                  useChatStore.setState((state) => {
                    const draftId = state.currentStream?.draftClientId;
                    if (!draftId) return {} as any;
                    const draft = state.messagesById[draftId];
                    if (!draft) return {} as any;
                    return {
                      messagesById: {
                        ...state.messagesById,
                        [draftId]: { ...draft, isDraft: false, status: 'error' as const },
                      },
                    } as any;
                  });
                } catch {}
                return;
              } else if (parsed.type === 'token' && parsed.data?.token) {
                if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 🔤 Token type detected');
                appendNow(parsed.data.token);
              } else if (parsed.type === 'stream' && parsed.chunk_type === 'content') {
                if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 📝 Content chunk detected');
                if (firstContentAt === undefined) firstContentAt = performance.now();
                contentEvents += 1;
                appendNow(parsed.content || '');
              } else if (parsed.type === 'stream' && parsed.chunk_type === 'done') {
                if (VERBOSE_STREAM_LOGS) console.log('[UnifiedSSEProviderV3] 🏁 Done chunk detected');
                if (doneAt === undefined) doneAt = performance.now();
                // Flush any remaining queued tokens before completing
                if (tokenQueueRef.current.length && flushTimerRef.current) {
                  try { clearInterval(flushTimerRef.current); } catch {}
                  flushTimerRef.current = null;
                  // Drain synchronously
                  while (tokenQueueRef.current.length) {
                    const next = tokenQueueRef.current.shift();
                    if (next) actions.appendToStream(next);
                  }
                }
                actions.completeStream();
                // Reconcile with backend persistence
                await reconcileOrPersistAssistant();
                // IMPORTANT: For side/threaded streams, do NOT hydrate the main lane
                try {
                  const st = useChatStore.getState();
                  const sideRun = Boolean(st.currentStream?.threadId || st.currentStream?.sideStream);
                  if (!sideRun) {
                    await quickReconcileAfterDone();
                  }
                } catch {
                  // non-fatal
                }
                if (metricsEnabled) {
                  const ttfbMs = firstByteAt !== undefined ? firstByteAt - requestStart : undefined;
                  const timeToFirstContentMs = firstContentAt !== undefined ? firstContentAt - requestStart : undefined;
                  const totalDurationMs = performance.now() - requestStart;
                  // eslint-disable-next-line no-console
                  console.table({
                    ttfbMs: ttfbMs?.toFixed(1),
                    timeToFirstContentMs: timeToFirstContentMs?.toFixed(1),
                    totalDurationMs: totalDurationMs?.toFixed(1),
                    contentEvents,
                    bytesRead: totalBytesReceived,
                    workerMode: headerWorkerMode || 'unknown',
                    requestId: headerReqId || headerClientReqId || 'n/a',
                  });
                }
                return;
              }
            } catch (e) {
              console.warn('[UnifiedSSEProviderV3] Failed to parse SSE data:', e);
            }
          }

          // Fallback: handle backend "AI SDK data-stream" line protocol (0:, 1:, d:, 3:)
          // Only consume lines that match these prefixes to avoid eating partial SSE frames
          while (true) {
            const nl = buffer.indexOf('\n');
            if (nl === -1) break;
            const rawLine = buffer.slice(0, nl).trim();
            buffer = buffer.slice(nl + 1);
            if (!rawLine) continue;

            try {
              if (rawLine.startsWith('0:')) {
                const contentJson = rawLine.slice(2);
                const content = JSON.parse(contentJson);
                if (typeof content === 'string' && content.length > 0) {
                  actions.appendToStream(content);
                }
              } else if (rawLine.startsWith('1:')) {
                // debug frame; ignore
              } else if (rawLine.startsWith('d:')) {
                actions.completeStream();
                void reconcileOrPersistAssistant();
                return;
              } else if (rawLine.startsWith('3:')) {
                let errMsg = rawLine.slice(2);
                try { const parsed = JSON.parse(errMsg); errMsg = typeof parsed === 'string' ? parsed : JSON.stringify(parsed); } catch {}
                actions.setStreamError(errMsg);
                // Mark the assistant draft as error instead of removing
                try {
                  useChatStore.setState((state) => {
                    const draftId = state.currentStream?.draftClientId;
                    if (!draftId) return {} as any;
                    const draft = state.messagesById[draftId];
                    if (!draft) return {} as any;
                    return {
                      messagesById: {
                        ...state.messagesById,
                        [draftId]: { ...draft, isDraft: false, status: 'error' as const },
                      },
                    } as any;
                  });
                } catch {}
                return;
              } else {
                // Unknown line; push back by prepending to buffer so it can be part of an SSE frame later
                buffer = rawLine + '\n' + buffer;
                break;
              }
            } catch (e) {
              console.warn('[UnifiedSSEProviderV3] Failed to parse raw line protocol:', e);
            }
          }
        }
        // Reader completed; finalize and reconcile/persist once
        // Ensure no queued tokens remain for the next stream
        try {
          if (flushTimerRef.current) {
            clearInterval(flushTimerRef.current);
            flushTimerRef.current = null;
          }
        } catch {}
        tokenQueueRef.current = [];
        actions.completeStream();
        // Reconcile with backend persistence (non-blocking) and optionally hydrate
        void (async () => {
          try {
            await reconcileOrPersistAssistant();
            const st = useChatStore.getState();
            const sideRun = Boolean(st.currentStream?.threadId || st.currentStream?.sideStream);
            if (!sideRun) {
              await quickReconcileAfterDone();
            }
          } catch {}
        })();
      } catch (error: any) {
        if (error.name === 'AbortError') {
          console.log('[UnifiedSSEProviderV3] Stream aborted');
          // On abort, clear any pending token buffer to avoid leakage into next stream
          try {
            if (flushTimerRef.current) {
              clearInterval(flushTimerRef.current);
              flushTimerRef.current = null;
            }
          } catch {}
          tokenQueueRef.current = [];
          return;
        }

        console.error('[UnifiedSSEProviderV3] Stream error:', error);
        actions.setStreamError(error.message);
      } finally {
        if (activeStreamRef.current === abortController) {
          activeStreamRef.current = null;
        }
      }
    };
    
    performStreaming();
    
    return () => {
      if (activeStreamRef.current) {
        activeStreamRef.current.abort();
        activeStreamRef.current = null;
      }
    };
  }, [isStreaming, conversationRef, token]); // Now using primitive values as deps!
  
  return <>{children}</>;
};
